// ── Agent Draft Types ──────────────────────────────────────────────────────────
//
// Domain types and API contracts for agent drafts (/v2/agents/drafts).
// Collection: agent_drafts — independent from the live agents collection.
// No Mongoose, no DB details. Those live in agent-draft.model.ts.

import { z } from "zod";
import { AgentKind, AgentExample, AgentResource } from "./agent.type";

// ── Enums ──────────────────────────────────────────────────────────────────────

export const AgentDraftStatus = {
  BACKLOG: "backlog",
  TODO: "todo",
  DOING: "doing",
  BLOCKED: "blocked",
  DONE: "done",
} as const;

export type AgentDraftStatus = (typeof AgentDraftStatus)[keyof typeof AgentDraftStatus];

export const AgentDraftPriority = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical",
} as const;

export type AgentDraftPriority = (typeof AgentDraftPriority)[keyof typeof AgentDraftPriority];

// ── Response DTO ───────────────────────────────────────────────────────────────

export const AgentDraft = z.object({
  id: z.string().describe("Unique resource identifier"),
  name: z.string().min(1).max(100).describe("Draft agent display name"),
  description: z.string().min(1).optional().describe("Agent purpose and capabilities"),
  status: z
    .enum(Object.values(AgentDraftStatus) as [string, ...string[]])
    .describe("Design workflow status"),
  vendor: z.string().min(1).describe("Target AI vendor (e.g. openai, anthropic, google)"),
  priority: z
    .enum(Object.values(AgentDraftPriority) as [string, ...string[]])
    .describe("Build priority"),
  team_id: z.string().min(1).describe("Owning team ID"),
  created_by: z.string().min(1).describe("Creator user ID"),
  created_at: z.coerce.date().describe("Creation timestamp"),
  updated_at: z.coerce.date().describe("Last update timestamp"),
});

export type AgentDraft = z.infer<typeof AgentDraft>;

// ── Create DTO ─────────────────────────────────────────────────────────────────

export const AgentDraftCreate = AgentDraft.omit({
  id: true,
  created_at: true,
  updated_at: true,
}).extend({
  status: z
    .enum(Object.values(AgentDraftStatus) as [string, ...string[]])
    .optional()
    .default(AgentDraftStatus.BACKLOG),
  priority: z
    .enum(Object.values(AgentDraftPriority) as [string, ...string[]])
    .optional()
    .default(AgentDraftPriority.MEDIUM),
});

export type AgentDraftCreate = z.infer<typeof AgentDraftCreate>;

// ── Update DTO ─────────────────────────────────────────────────────────────────
// Immutable after creation: team_id, created_by
// status and priority are re-declared without .default() so Zod does not inject
// defaults for absent fields on PATCH, which would overwrite the stored values.

export const AgentDraftUpdate = AgentDraftCreate.omit({
  team_id: true,
  created_by: true,
})
  .partial()
  .extend({
    status: z.enum(Object.values(AgentDraftStatus) as [string, ...string[]]).optional(),
    priority: z.enum(Object.values(AgentDraftPriority) as [string, ...string[]]).optional(),
  });

export type AgentDraftUpdate = z.infer<typeof AgentDraftUpdate>;

// ── Publish body ───────────────────────────────────────────────────────────────
// Fields that Agent requires but AgentDraft doesn't carry.
// Draft provides: name, description, team_id, created_by.
// Caller provides: type, owner_id, and any optional Agent fields.

export const AgentDraftPublishBody = z.object({
  type: z.enum(Object.values(AgentKind) as [string, ...string[]]).describe("Agent framework kind"),
  owner_id: z.string().min(1).describe("Owner user ID for the published agent"),
  // Optional Agent fields — passed through to AgentCreate
  alias: z.string().optional().describe("URL-safe slug (auto-generated from name if omitted)"),
  monthly_budget: z.number().min(0).optional().describe("Monthly budget limit"),
  roles: z.array(z.string()).optional().describe("Agent roles"),
  integrations: z.array(z.string()).optional().describe("Enabled integration IDs"),
  examples: z.array(AgentExample).optional().describe("Input/output examples"),
  resources: z.array(AgentResource).optional().describe("Linked knowledge resources"),
  config: z.record(z.string(), z.unknown()).optional().describe("Arbitrary agent configuration"),
});

export type AgentDraftPublishBody = z.infer<typeof AgentDraftPublishBody>;
